import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { Loader2 } from 'lucide-react';
import { cloneElement, forwardRef, isValidElement, useMemo } from 'react';
import { LinkTooltip } from './LinkTooltip';
import type { GenericIconButtonLinkProps, IconButtonProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Coloured icon button with customisable colour themes
 *
 * Supports 10 colour options with proper hover states.
 * Available in bordered and borderless (default) variants.
 *
 * @example
 * // Borderless (default)
 * <Button.Icon icon={<Star />} color="gold" />
 * <Button.Icon icon={<Trash2 />} color="red" />
 *
 * @example
 * // Bordered
 * <Button.Icon icon={<Settings />} color="blue" bordered />
 *
 * @example
 * // With tooltip
 * <Button.Icon icon={<Bookmark />} color="purple" tooltip="Save" />
 */
export const ButtonIcon = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            icon,
            color = 'accent',
            bordered = false,
            filled = false,
            size = 'middle',
            tooltip,
            tooltipDisabled = false,
            onClick,
            onKeyDown,
            disabled,
            loading = false,
            className,
            'aria-label': ariaLabel,
            'aria-expanded': ariaExpanded,
            'aria-haspopup': ariaHasPopup,
            'aria-controls': ariaControls,
        },
        ref,
    ) => {
        const handleClick = (e: React.MouseEvent) => {
            if (onClick) {
                e.stopPropagation();
                onClick(e);
            }
        };

        const buttonClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'btn-icon-colored',
                    `btn-color-${color}`,
                    sizeClasses[size],
                    bordered && 'bordered',
                    filled && 'filled',
                    loading && 'loading',
                    className,
                ),
            [color, size, bordered, filled, loading, className],
        );

        const renderIcon = () => {
            if (loading) {
                return <Loader2 className="btn-icon animate-spin" size={iconSizes[size]} />;
            }
            if (icon && isValidElement(icon)) {
                const sizedIcon = cloneElement(icon as React.ReactElement<{ size?: number }>, {
                    size: iconSizes[size],
                });
                return <span className="btn-icon">{sizedIcon}</span>;
            }
            return icon ? <span className="btn-icon">{icon}</span> : null;
        };

        const button = (
            <button
                ref={ref}
                type="button"
                disabled={disabled || loading}
                aria-busy={loading}
                // Icon-only buttons have no text content — without a label they are
                // unnamed to assistive tech. A string tooltip is the visible name,
                // so it doubles as the accessible one unless overridden.
                aria-label={ariaLabel ?? (typeof tooltip === 'string' ? tooltip : undefined)}
                aria-expanded={ariaExpanded}
                aria-haspopup={ariaHasPopup}
                aria-controls={ariaControls}
                className={buttonClasses}
                onClick={handleClick}
                onKeyDown={onKeyDown}
            >
                {renderIcon()}
            </button>
        );

        // Presence check, not truthiness: 0 is a valid ReactNode title (antd renders it).
        if (tooltip || tooltip === 0) {
            // Focus opens the tooltip only for rich (ReactNode) content, which cannot be
            // conveyed by the accessible name and so must stay keyboard-reachable
            // (WCAG 1.4.13). String tooltips remain hover-only: their text is already
            // announced (as the name, or alongside a caller-supplied aria-label), and a
            // focus-opened overlay would be redundant and linger over keyboard-opened
            // dropdowns/popconfirms.
            const tooltipAddsInfo = typeof tooltip !== 'string';
            return (
                <Tooltip
                    title={tooltip}
                    open={tooltipDisabled ? false : undefined}
                    trigger={tooltipAddsInfo ? ['hover', 'focus'] : ['hover']}
                >
                    {button}
                </Tooltip>
            );
        }

        return button;
    },
);

ButtonIcon.displayName = 'ButtonIcon';

/**
 * Base anchor component for generic icon button links
 * Styled as a coloured icon button using ButtonIcon CSS classes
 */
const ButtonIconLinkBase = forwardRef<HTMLAnchorElement, GenericIconButtonLinkProps>(
    (
        {
            icon,
            color = 'accent',
            bordered = false,
            size = 'middle',
            tooltip,
            disabled,
            disabledReason,
            className,
            ...props
        },
        ref,
    ) => {
        const linkClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'btn-icon-colored',
                    `btn-color-${color}`,
                    sizeClasses[size],
                    bordered && 'bordered',
                    disabled && 'pointer-events-none opacity-50',
                    className,
                ),
            [color, size, bordered, disabled, className],
        );

        const renderIcon = () => {
            if (icon && isValidElement(icon)) {
                const sizedIcon = cloneElement(icon as React.ReactElement<{ size?: number }>, {
                    size: iconSizes[size],
                });
                return <span className="btn-icon">{sizedIcon}</span>;
            }
            return icon ? <span className="btn-icon">{icon}</span> : null;
        };

        const link = (
            <a
                ref={ref}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : undefined}
                // Icon-only link: the hover tooltip is not an accessible name, so a string tooltip doubles
                // as one (axe `link-name`). The label is set after the spread so a forwarded `undefined` cannot erase it.
                className={linkClasses}
                {...props}
                aria-label={props['aria-label'] ?? (typeof tooltip === 'string' ? tooltip : undefined)}
            >
                {renderIcon()}
            </a>
        );

        return (
            <LinkTooltip disabled={disabled} disabledReason={disabledReason} tooltip={tooltip}>
                {link}
            </LinkTooltip>
        );
    },
);

ButtonIconLinkBase.displayName = 'ButtonIconLinkBase';

/**
 * Button.Icon.Link - Type-safe router link styled as a coloured icon button
 *
 * Uses TanStack Router for type-safe `to`, `params`, and `search` props.
 * Supports all 10 colour options and bordered/borderless variants.
 *
 * @example
 * // Navigate with a custom icon and colour
 * <Button.Icon.Link
 *   icon={<Star />}
 *   color="gold"
 *   to="/console/$orgId/favourites"
 *   params={{ orgId: '1' }}
 *   tooltip="View favourites"
 * />
 *
 * @example
 * // Bordered variant
 * <Button.Icon.Link
 *   icon={<Settings />}
 *   color="blue"
 *   bordered
 *   to="/console/$orgId/settings"
 *   params={{ orgId: '1' }}
 * />
 */
export const ButtonIconLink: LinkComponent<typeof ButtonIconLinkBase> = createLink(ButtonIconLinkBase);
